/** 与 3.0 一键排版相同：自动换行写入字面量 `\n`，不用 `|n`。 */
const AUTO_WRAP_MARKER = '\\n'
let measureCanvas: HTMLCanvasElement | null = null
let lastWrapCacheKey = ''
let lastWrapCacheValue = ''

function getMeasureContext(): CanvasRenderingContext2D | null {
  if (typeof document === 'undefined') return null
  if (!measureCanvas) {
    measureCanvas = document.createElement('canvas')
  }
  return measureCanvas.getContext('2d')
}

function readTextControlToken(text: string, index: number): { token: string; measureText: string } | null {
  const rest = text.slice(index)
  const colorMatch = rest.match(/^\\c\[\s*\d{1,3}\s*,\s*\d{1,3}\s*,\s*\d{1,3}\s*\]/i)
  if (colorMatch?.[0]) {
    return { token: colorMatch[0], measureText: '' }
  }
  const variableMatch = rest.match(/^\\[vxt]\[\s*-?\d+\s*\]/i)
  if (variableMatch?.[0]) {
    return { token: variableMatch[0], measureText: variableMatch[0] }
  }
  return null
}

function measureRunWidth(
  text: string,
  measureCharWidth: ((char: string) => number) | undefined,
  ctx: CanvasRenderingContext2D | null,
): number {
  if (!text) return 0
  if (measureCharWidth) {
    let width = 0
    for (const char of Array.from(text)) {
      width += measureCharWidth(char)
    }
    return width
  }
  return ctx?.measureText(text).width ?? 0
}

function wrapLine(
  line: string,
  maxWidth: number,
  measureCharWidth: ((char: string) => number) | undefined,
  ctx: CanvasRenderingContext2D | null,
): string {
  let output = ''
  let measuredWidth = 0
  for (let index = 0; index < line.length;) {
    const controlToken = readTextControlToken(line, index)
    if (controlToken) {
      const tokenWidth = measureRunWidth(controlToken.measureText, measureCharWidth, ctx)
      if (controlToken.measureText && measuredWidth && measuredWidth + tokenWidth > maxWidth) {
        output += AUTO_WRAP_MARKER
        measuredWidth = 0
      }
      output += controlToken.token
      measuredWidth += tokenWidth
      index += controlToken.token.length
      continue
    }
    const char = Array.from(line.slice(index))[0] ?? ''
    if (!char) break
    const charWidth = measureRunWidth(char, measureCharWidth, ctx)
    if (measuredWidth && measuredWidth + charWidth > maxWidth) {
      output += AUTO_WRAP_MARKER
      measuredWidth = 0
    }
    output += char
    measuredWidth += charWidth
    index += char.length
  }
  return output
}

/** 编辑器可见换行：旧 `|n` 丢掉后按当前宽度重算；`\n` `/n` 显示成真换行。 */
export function decodeDialogTextForEdit(value: string): string {
  return String(value || '')
    .replace(/\r\n/g, '\n')
    .replace(/\|n/g, '')
    .replace(/\\n/g, '\n')
    .replace(/\/n/g, '\n')
}

/** 写回事件：手动回车和自动折行都写成字面量 `\n`（与 3.0 相同）。 */
export function encodeDialogTextFromEdit(params: {
  text: string
  enableAutoWrap: boolean
  autoWrapWidth: number
  autoWrapFont: string
  measureCharWidth?: (char: string) => number
}): string {
  const normalizedText = String(params.text || '').replace(/\r\n/g, '\n')
  const manualLines = normalizedText.split('\n')
  if (!params.enableAutoWrap || params.autoWrapWidth <= 0 || !normalizedText) {
    return manualLines.join(AUTO_WRAP_MARKER)
  }
  const cacheKey = `${params.autoWrapWidth}|${params.autoWrapFont}|${normalizedText}`
  if (cacheKey === lastWrapCacheKey) return lastWrapCacheValue
  const ctx = params.measureCharWidth ? null : getMeasureContext()
  if (!params.measureCharWidth && !ctx) return manualLines.join(AUTO_WRAP_MARKER)
  if (ctx) ctx.font = params.autoWrapFont
  const wrapped = manualLines
    .map((line) => wrapLine(line, params.autoWrapWidth, params.measureCharWidth, ctx))
    .join(AUTO_WRAP_MARKER)
  lastWrapCacheKey = cacheKey
  lastWrapCacheValue = wrapped
  return wrapped
}

/** 将运行时文本换行标记统一成预览可用换行。 */
export function normalizeDialogTextForPreview(value: string): string {
  return String(value || '')
    .replace(/\r\n/g, '\n')
    .replace(/\|n/g, '\n')
    .replace(/\\n/g, '\n')
    .replace(/\/n/g, '\n')
}
